import { createLogger } from './logger'

const log = createLogger('VitePreloadRecovery')

// Vite dispatches vite:preloadError on window when a dynamic import() rejects
// -- reloading gets a fresh page with the current deploy's hashes.
//
// Timestamp, not boolean, because reload() can be cancelled (e.g. a page's
// unsaved-changes prompt) and two near-simultaneous failures shouldn't both
// trigger a reload or flash the fallback.
const RELOAD_GUARD_KEY = 'vite-preload-reloaded-at'
const RELOAD_IN_FLIGHT_MS = 2_000
const RELOAD_GUARD_WINDOW_MS = 10_000

// Fails safe to the fallback if sessionStorage can't be read reliably.
function guardState() {
  try {
    const storedAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY))
    if (!Number.isFinite(storedAt)) return 'expired'
    const age = Date.now() - storedAt
    if (age < RELOAD_IN_FLIGHT_MS) return 'in-flight'
    if (age < RELOAD_GUARD_WINDOW_MS) return 'recent'
    return 'expired'
  } catch {
    return 'recent'
  }
}

// setItem can throw independently of getItem (e.g. quota exceeded); false
// here means fallback, not an unguarded reload loop.
function markReloadAttempt() {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
    return true
  } catch {
    return false
  }
}

// jsdom's window.location.reload can't be spied on directly; this
// indirection gives tests a seam.
export const browserActions = {
  reload() {
    window.location.reload()
  },
}

// Plain HTML, not React: fires outside any mounted tree, so there's no
// TranslationProvider to read copy from (same as public/500.html).
function renderFallback() {
  document.body.innerHTML = `
    <div role="alert" aria-live="assertive" style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;height:100vh;font-family:ui-sans-serif,system-ui,sans-serif;text-align:center;padding:24px;">
      <p style="font-size:14px;color:#4b5563;">Something went wrong loading this page.</p>
      <button id="vite-preload-retry" style="padding:8px 16px;border-radius:6px;border:1px solid #d1d5db;background:#fff;cursor:pointer;font-size:14px;">Retry</button>
    </div>
  `
  document.getElementById('vite-preload-retry').addEventListener('click', () => {
    browserActions.reload()
  })
}

export function handleVitePreloadError(event) {
  // Vite re-throws the original error after this event unless prevented.
  event.preventDefault()
  log.warn('Stale chunk failed to load')

  const state = guardState()
  if (state === 'in-flight') return
  if (state === 'recent') {
    renderFallback()
    return
  }
  if (!markReloadAttempt()) {
    renderFallback()
    return
  }
  browserActions.reload()
}
